import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import EmailDialog from "./customers/EmailDialog";

import CustomerSearch from "./customers/CustomerSearch";
import CustomerCard from "./customers/CustomerCard";
import PDFPreviewDialog from "./customers/PDFPreviewDialog";
import EditCustomerDialog, { EditCustomerFormValues } from "./customers/EditCustomerDialog";

import { convertBankDetails, getReviewLinkUrl } from "@/utils/paymentHelpers";
import { useBusinessProfile } from "@/hooks/useBusinessProfile";
import { sendEmail } from "@/utils/email/api";
import { createPaymentLinkEmailMessage } from "@/utils/email/templates/paymentTemplate";
import { renderPaymentRequestTemplate, renderPaymentRequestEmailHtml } from "@/utils/paymentTemplate";
import { loadPaymentSettings, DEFAULT_PAYMENT_REMINDER_TEMPLATE, DEFAULT_PAYMENT_RECEIVED_TEMPLATE } from "@/hooks/usePaymentSettings";
import { base64ToBlob } from "@/utils/pdfGenerator";
import { updateCustomerEmail } from "@/utils/customerUpdateUtils";
import { generateCustomerReceipt } from "@/utils/customerReceiptGenerator";
import { getDocumentMeta, type DocumentType } from "@/utils/documentMeta";
import type { ReceiptData } from "@/utils/pdfGenerator";
import { uploadReceiptPdf } from "@/utils/pdf/uploadReceipt";
import { shortenUrl } from "@/utils/shortLink";

interface Customer {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city: string | null;
  postcode: string | null;
  payment_status: string;
  created_at: string;
  updated_at: string;
  payment_links: {
    id: string;
    amount: number;
    currency: string;
    description: string | null;
    review_link_included: boolean | null;
  } | null;
}

interface CustomersListProps {
  refreshTrigger: number;
  dateRange: { from: Date; to: Date };
}

const CustomersList = ({ refreshTrigger, dateRange }: CustomersListProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { businessProfile } = useBusinessProfile();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<'all' | 'paid' | 'overdue' | 'estimate'>('all');
  const [emailDialogAction, setEmailDialogAction] = useState<'reminder' | 'receipt'>('reminder');

  // Filter customers based on search query and status
  const filteredCustomers = useMemo(() => {
    let list = customers;

    if (statusFilter !== 'all') {
      list = list.filter(c => (c.payment_status || '').toLowerCase() === statusFilter);
    }

    if (!searchQuery.trim()) return list;

    const query = searchQuery.toLowerCase();
    return list.filter(customer =>
      customer.name?.toLowerCase().includes(query) ||
      customer.email?.toLowerCase().includes(query) ||
      customer.phone?.includes(query) ||
      customer.payment_links?.description?.toLowerCase().includes(query) ||
      customer.address?.toLowerCase().includes(query) ||
      customer.city?.toLowerCase().includes(query) ||
      customer.postcode?.toLowerCase().includes(query)
    );
  }, [customers, searchQuery, statusFilter]);

  const statusCounts = useMemo(() => ({
    all: customers.length,
    paid: customers.filter(c => (c.payment_status || '').toLowerCase() === 'paid').length,
    overdue: customers.filter(c => (c.payment_status || '').toLowerCase() === 'overdue').length,
    estimate: customers.filter(c => (c.payment_status || '').toLowerCase() === 'estimate').length,
  }), [customers]);

  const fetchCustomers = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Format dates to ensure we're comparing date-only values
      const fromDate = new Date(dateRange.from);
      fromDate.setHours(0, 0, 0, 0);
      const toDate = new Date(dateRange.to);
      toDate.setHours(23, 59, 59, 999);

      console.log("Fetching customers for date range:", {
        from: fromDate.toISOString(),
        to: toDate.toISOString()
      });

      const { data, error } = await supabase
        .from('customers')
        .select(`
          *,
          payment_links:payment_link_id (
            id,
            amount,
            currency,
            description,
            review_link_included
          )
        `)
        .eq('user_id', user.id)
        .gte('created_at', fromDate.toISOString())
        .lte('created_at', toDate.toISOString())
        .order('created_at', { ascending: false });

      if (error) {
        console.error("Error fetching customers:", error);
        toast({
          title: "Error",
          description: "Failed to fetch customers",
          variant: "destructive"
        });
        return;
      }

      console.log("Fetched customers with payment data:", data?.length || 0);
      setCustomers(data || []);
    } catch (error) {
      console.error("Unexpected error fetching customers:", error);
      toast({
        title: "Error",
        description: "Failed to fetch customers",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, [refreshTrigger, dateRange, user]);

  const handleStatusUpdate = async (customerId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('customers')
        .update({ payment_status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', customerId);

      if (error) {
        console.error("Error updating customer status:", error);
        toast({
          title: "Error",
          description: "Failed to update customer status",
          variant: "destructive"
        });
        return;
      }

      // Update the customer locally in state instead of re-fetching
      setCustomers(prevCustomers => 
        prevCustomers.map(customer => 
          customer.id === customerId 
            ? { ...customer, payment_status: newStatus, updated_at: new Date().toISOString() }
            : customer
        )
      );
      
      // Dispatch event for dashboard to refresh stats
      window.dispatchEvent(new CustomEvent('customerStatusChanged'));
      
      toast({
        title: "Success",
        description: "Customer status updated successfully"
      });

      // Auto-send "Payment Received" confirmation when marking as paid
      if (newStatus === 'paid') {
        const customer = customers.find(c => c.id === customerId);
        if (customer) {
          const amount = customer.payment_links?.amount || 0;
          const currency = customer.payment_links?.currency || 'GBP';
          const description = customer.payment_links?.description || '';
          const businessName = businessProfile?.businessName || 'Our Business';
          const safeFromName = businessName.replace(/["<>,]/g, "").trim() || "Notifications";
          const formattedBankDetails = businessProfile?.bankDetails ? convertBankDetails(businessProfile.bankDetails) : undefined;
          const savedSettings = loadPaymentSettings(user?.id);
          const receivedTemplate = savedSettings.paymentReceivedTemplate || DEFAULT_PAYMENT_RECEIVED_TEMPLATE;
          const receivedBody = renderPaymentRequestTemplate(receivedTemplate, {
            customerName: customer.name || 'Customer',
            businessName,
            amount,
            currency,
            description,
            accountName: formattedBankDetails?.accountName,
            accountNumber: formattedBankDetails?.accountNumber,
            sortCode: formattedBankDetails?.sortCode,
          });

          // Only include review link if original payment request had it enabled
          const receivedReviewUrl =
            customer.payment_links?.review_link_included && businessProfile?.primary_review_link
              ? getReviewLinkUrl(businessProfile.primary_review_link, businessProfile)
              : undefined;
          const receivedBodyWithReview = receivedReviewUrl
            ? `${receivedBody}\n\nLeave us a review: ${receivedReviewUrl}`
            : receivedBody;

          // Generate PDF once for both email + SMS link
          let pdfDataUri: string | null = null;
          let shortPdfUrl: string | null = null;
          try {
            pdfDataUri = await generateCustomerReceipt(customer as any, businessProfile);
            if (user?.id && pdfDataUri) {
              const uploadId = customer.payment_links?.id || customer.id;
              const base64 = pdfDataUri.split(',')[1];
              const publicUrl = await uploadReceiptPdf(base64, user.id, uploadId);
              if (publicUrl) shortPdfUrl = await shortenUrl(publicUrl, user.id);
            }
          } catch (pdfErr) {
            console.error("Auto-send PDF prep failed:", pdfErr);
          }

          // Email with PDF receipt
          if (customer.email && pdfDataUri) {
            try {
              const html = renderPaymentRequestEmailHtml(receivedBodyWithReview, {
                logoUrl: savedSettings.includeLogo ? (businessProfile as any)?.companyLogoUrl : undefined,
                businessName,
              });
              const result = await sendEmail({
                to: customer.email,
                subject: `Payment Received From ${businessName}`,
                html,
                from: `${safeFromName} <notifications@houndpayments.co.uk>`,
                userId: user!.id,
                emailType: 'receipt',
                attachments: [{
                  filename: `receipt-${customer.name || 'customer'}-${customer.id.substring(0, 8)}.pdf`,
                  content: pdfDataUri.split(',')[1],
                  type: 'application/pdf',
                }],
              });
              if (result.success) {
                toast({ title: "Receipt Emailed", description: `Receipt sent to ${customer.email}` });
              } else {
                throw new Error(result.error || "Failed to send receipt email");
              }
            } catch (emailErr: any) {
              console.error("Auto-send receipt failed:", emailErr);
              toast({
                title: "Receipt Email Failed",
                description: emailErr?.message || "Could not email receipt — status was still updated.",
                variant: "destructive"
              });
            }
          }

          // SMS confirmation
          if (customer.phone) {
            try {
              const smsBody = shortPdfUrl
                ? `${receivedBodyWithReview}\n\nPDF: ${shortPdfUrl}`
                : receivedBodyWithReview;
              const { data: smsResult, error: smsError } = await supabase.functions.invoke('send-sms', {
                body: { phoneNumber: customer.phone, message: smsBody },
              });
              if (smsError) throw new Error(smsError.message || 'Failed to send SMS');
              if (!smsResult?.success) throw new Error(smsResult?.error || 'Failed to send SMS');
              toast({ title: "Confirmation SMS Sent", description: `Sent to ${customer.phone}` });
            } catch (smsErr: any) {
              console.error("Auto-send confirmation SMS failed:", smsErr);
              toast({
                title: "Confirmation SMS Failed",
                description: smsErr?.message || "Could not send SMS — status was still updated.",
                variant: "destructive"
              });
            }
          }

        }
      }
    } catch (error) {
      console.error("Unexpected error updating customer status:", error);
      toast({
        title: "Error",
        description: "Failed to update customer status",
        variant: "destructive"
      });
    }
  };

  // Shared helper: send a receipt PDF to a customer's email
  const sendReceiptEmail = async (customer: Customer, pdfData: string) => {
    if (!user) {
      return { success: false, error: "User information not available" };
    }
    if (!customer.email) {
      return { success: false, error: "Customer has no email address" };
    }

    const base64Content = pdfData.split(',')[1];
    const businessName = businessProfile?.businessName || 'Our Business';
    const safeFromName = businessName.replace(/["<>,]/g, "").trim() || "Notifications";
    const amount = customer.payment_links?.amount || 0;
    const currency = customer.payment_links?.currency || 'GBP';

    return await sendEmail({
      to: customer.email,
      subject: `Receipt - ${currency}${amount}${customer.payment_links?.description ? ` - ${customer.payment_links.description}` : ''}`,
      html: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2>Payment Receipt</h2>
          <p>Dear ${customer.name || 'Customer'},</p>
          <p>Please find attached your payment receipt.</p>
          <p>Amount: ${currency}${amount}</p>
          ${customer.payment_links?.description ? `<p>Description: ${customer.payment_links.description}</p>` : ''}
          <p>Thank you for your business!</p>
          <p>Best regards,<br>${businessName}</p>
        </div>
      `,
      from: `${safeFromName} <notifications@houndpayments.co.uk>`,
      userId: user.id,
      emailType: 'receipt',
      attachments: [{
        filename: `receipt-${customer.name || 'customer'}-${customer.id.substring(0, 8)}.pdf`,
        content: base64Content,
        type: 'application/pdf'
      }]
    });
  };

  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async (values: EditCustomerFormValues) => {
    if (!editingCustomer) return;
    setSavingEdit(true);
    try {
      const updates = {
        name: values.name.trim(),
        email: values.email?.trim() || null,
        phone: values.phone?.trim() || null,
        address: values.address?.trim() || null,
        city: values.city?.trim() || null,
        postcode: values.postcode?.trim() || null,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('customers')
        .update(updates)
        .eq('id', editingCustomer.id);

      if (error) {
        console.error("Error updating customer:", error);
        toast({
          title: "Error",
          description: "Failed to update customer",
          variant: "destructive",
        });
        return;
      }

      setCustomers(prev => prev.map(c => c.id === editingCustomer.id ? { ...c, ...updates } : c));
      toast({ title: "Success", description: "Customer details updated" });
      setEditDialogOpen(false);
      setEditingCustomer(null);
    } catch (err) {
      console.error("Unexpected error updating customer:", err);
      toast({
        title: "Error",
        description: "Failed to update customer",
        variant: "destructive",
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteCustomer = async (customer: Customer) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete ${customer.name || 'this customer'}? This action cannot be undone.`
    );
    
    if (!confirmDelete) return;

    try {
      const { error } = await supabase
        .from('customers')
        .delete()
        .eq('id', customer.id);

      if (error) {
        console.error("Error deleting customer:", error);
        toast({
          title: "Error",
          description: "Failed to delete customer",
          variant: "destructive"
        });
        return;
      }

      // Remove the customer locally from state
      setCustomers(prevCustomers => 
        prevCustomers.filter(c => c.id !== customer.id)
      );
      
      // Dispatch event for dashboard to refresh stats
      window.dispatchEvent(new CustomEvent('customerStatusChanged'));
      
      toast({
        title: "Success",
        description: "Customer deleted successfully"
      });
    } catch (error) {
      console.error("Unexpected error deleting customer:", error);
      toast({
        title: "Error",
        description: "Failed to delete customer",
        variant: "destructive"
      });
    }
  };

  const handleEmailReminder = (customer: Customer) => {
    setSelectedCustomer(customer);
    setEmailDialogAction('reminder');
    setEmailDialogOpen(true);
  };

  const sendSmsWithQuotaToast = async (phoneNumber: string, message: string) => {
    const { data, error } = await supabase.functions.invoke('send-sms', {
      body: { phoneNumber, message },
    });
    const errorBody = (error as any)?.context?.body ?? data;
    const errorCode =
      data?.error ||
      (typeof errorBody === 'string'
        ? (() => { try { return JSON.parse(errorBody).error; } catch { return null; } })()
        : errorBody?.error);
    if (errorCode === 'sms_quota_exceeded') {
      toast({
        title: 'SMS limit reached',
        description: "You've used all SMS for this cycle. Top up from the payment form to send more.",
        variant: 'destructive',
      });
      return false;
    }
    if (error) throw new Error(error.message || 'Failed to send SMS');
    if (!data?.success) throw new Error(data?.error || 'Failed to send SMS');
    return true;
  };

  const getCustomerDocumentType = (customer: Customer): DocumentType => {
    if (customer.payment_status === 'estimate') return 'estimate';
    if (customer.payment_status === 'paid') return 'receipt';
    return 'invoice';
  };

  const openStoredCustomerPdf = async (customer: Customer) => {
    const paymentId = customer.payment_links?.id;
    if (!user?.id || !paymentId) {
      toast({
        title: 'PDF unavailable',
        description: 'This customer does not have a linked payment record.',
        variant: 'destructive',
      });
      return;
    }

    const path = `${user.id}/${paymentId}.pdf`;
    const { data: publicPdf } = supabase.storage.from('receipts').getPublicUrl(path);
    const openPdf = (url: string) => {
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    };

    try {
      const head = await fetch(publicPdf.publicUrl, { method: 'HEAD', cache: 'no-store' });
      if (head.ok) {
        openPdf(publicPdf.publicUrl);
        return;
      }
    } catch (err) {
      console.warn('Stored PDF check failed, attempting to recreate it:', err);
    }

    try {
      const docType = getCustomerDocumentType(customer);
      const meta = getDocumentMeta(docType);
      const savedSettings = loadPaymentSettings(user.id);
      const amount = customer.payment_links?.amount || 0;
      const currency = customer.payment_links?.currency || 'GBP';
      const description = customer.payment_links?.description || meta.label;
      const formattedBankDetails = businessProfile?.bankDetails
        ? convertBankDetails(businessProfile.bankDetails)
        : undefined;
      let dueDate: string | undefined;
      if (docType === 'invoice' && (savedSettings.paymentDueDays ?? 0) > 0) {
        const d = new Date(customer.created_at);
        d.setDate(d.getDate() + Number(savedSettings.paymentDueDays));
        dueDate = d.toLocaleDateString('en-GB');
      }

      const pdfBase64 = await meta.generatePdf({
        receiptNumber: `${docType.toUpperCase()}-${customer.id.substring(0, 8).toUpperCase()}`,
        date: new Date(customer.created_at).toLocaleDateString('en-GB'),
        amount,
        currency,
        description,
        customerName: customer.name || undefined,
        customerEmail: customer.email || undefined,
        customerPhone: customer.phone || undefined,
        customerAddress:
          customer.address || customer.city || customer.postcode
            ? {
                street: customer.address || undefined,
                city: customer.city || undefined,
                postcode: customer.postcode || undefined,
              }
            : undefined,
        businessProfile,
        bankDetails: formattedBankDetails,
        includeAddress: !!(customer.address || customer.city || customer.postcode),
        dueDate,
        receiptSettings: savedSettings,
      });

      const uploadedUrl = await uploadReceiptPdf(pdfBase64, user.id, paymentId);
      if (!uploadedUrl) throw new Error('Upload failed');
      openPdf(uploadedUrl);
    } catch (err) {
      console.error('Failed to open stored customer PDF:', err);
      toast({
        title: 'PDF unavailable',
        description: 'Could not load or recreate the Supabase PDF for this customer.',
        variant: 'destructive',
      });
    }
  };

  const handleSendReminderDirect = async (customer: Customer) => {
    setSelectedCustomer(customer);
    if (!customer.email && !customer.phone) {
      toast({
        title: "Contact required",
        description: "Add an email or phone number to send a reminder.",
        variant: "destructive",
      });
      return;
    }
    if (customer.email) await handleEmailReminderSend(customer.email, customer);
    if (customer.phone) {
      try {
        const businessName = businessProfile?.businessName || 'Our Business';
        const amount = customer.payment_links?.amount || 0;
        const currency = customer.payment_links?.currency || 'GBP';
        const formattedBankDetails = businessProfile?.bankDetails ? convertBankDetails(businessProfile.bankDetails) : undefined;
        const savedSettings = loadPaymentSettings(user?.id);
        const template = savedSettings.paymentReminderTemplate || DEFAULT_PAYMENT_REMINDER_TEMPLATE;
        const body = renderPaymentRequestTemplate(template, {
          customerName: customer.name || 'Customer',
          businessName,
          amount,
          currency,
          description: customer.payment_links?.description || '',
          accountName: formattedBankDetails?.accountName,
          accountNumber: formattedBankDetails?.accountNumber,
          sortCode: formattedBankDetails?.sortCode,
          dueDays: savedSettings.paymentDueDays,
        });
        const ok = await sendSmsWithQuotaToast(customer.phone, body);
        if (ok) toast({ title: 'SMS Sent', description: `Reminder sent to ${customer.phone}` });
      } catch (err: any) {
        toast({ title: 'SMS Failed', description: err?.message || 'Could not send SMS', variant: 'destructive' });
      }
    }
  };

  const handleEmailReminderSend = async (email: string, customerOverride?: Customer) => {
    const customer = customerOverride || selectedCustomer;
    if (!customer || !user) {
      toast({
        title: "Error",
        description: "Missing customer or user information",
        variant: "destructive"
      });
      return;
    }

    setSendingEmail(true);
    
    try {
      // If customer doesn't have email, update it first
      if (!customer.email && email !== customer.email) {
        console.log("Updating customer email before sending reminder...");
        const updateResult = await updateCustomerEmail(customer.id, email);
        
        if (!updateResult.success) {
          throw new Error(updateResult.error || "Failed to update customer email");
        }
        
        // Update the local customer data
        setSelectedCustomer(prev => prev ? { ...prev, email } : null);
        
        // Refresh the customer list to show updated email
        fetchCustomers();
        
        toast({
          title: "Customer Updated",
          description: "Email address added to customer record",
        });
      }

      // Gather payment information
      const amount = customer.payment_links?.amount || 0;
      const currency = customer.payment_links?.currency || 'GBP';
      const description = customer.payment_links?.description || '';
      const customerName = customer.name || 'Customer';
      const businessName = businessProfile?.businessName || 'Our Business';

      // Convert bank details to the format expected by email templates
      const formattedBankDetails = businessProfile?.bankDetails ? convertBankDetails(businessProfile.bankDetails) : undefined;

      // Render reminder body from the saved Payment Reminder Template
      const savedSettings = loadPaymentSettings(user.id);
      const reminderTemplate = savedSettings.paymentReminderTemplate || DEFAULT_PAYMENT_REMINDER_TEMPLATE;
      const reminderBody = renderPaymentRequestTemplate(reminderTemplate, {
        customerName,
        businessName,
        amount,
        currency,
        description,
        accountName: formattedBankDetails?.accountName,
        accountNumber: formattedBankDetails?.accountNumber,
        sortCode: formattedBankDetails?.sortCode,
        dueDays: savedSettings.paymentDueDays,
      });

      const emailContent = renderPaymentRequestEmailHtml(reminderBody, {
        logoUrl: savedSettings.includeLogo ? businessProfile?.businessLogo : undefined,
        businessName,
      });

      const safeFromName = businessName.replace(/["<>,]/g, "").trim() || "Notifications";

      // Send the email with reminder subject
      const result = await sendEmail({
        to: email,
        subject: `Payment Reminder From ${businessName}`,
        html: emailContent,
        from: `${safeFromName} <notifications@houndpayments.co.uk>`,
        userId: user.id,
        emailType: 'reminder'
      });

      if (result.success) {
        toast({
          title: "Email Sent",
          description: `Payment reminder sent successfully to ${email}`,
          variant: "default"
        });
        
        console.log("Email reminder sent successfully:", {
          customerId: customer.id,
          email: email,
          amount: amount,
          messageId: result.id,
          includedReviewLink: false
        });
      } else {
        throw new Error(result.error || "Failed to send email");
      }

    } catch (error) {
      console.error("Error sending email reminder:", error);
      toast({
        title: "Email Failed",
        description: `Failed to send email reminder: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleEmailReceiptSend = async (email: string) => {
    if (!selectedCustomer || !user) {
      toast({
        title: "Error",
        description: "Missing customer or user information",
        variant: "destructive"
      });
      return;
    }

    try {
      // If customer doesn't have email, update it first
      if (!selectedCustomer.email && email !== selectedCustomer.email) {
        console.log("Updating customer email before opening receipt preview...");
        const updateResult = await updateCustomerEmail(selectedCustomer.id, email);
        
        if (!updateResult.success) {
          throw new Error(updateResult.error || "Failed to update customer email");
        }
        
        // Update the local customer data
        setSelectedCustomer(prev => prev ? { ...prev, email } : null);
        
        // Refresh the customer list to show updated email
        fetchCustomers();
        
        toast({
          title: "Customer Updated",
          description: "Email address added to customer record",
        });
      }

      // After email is added/confirmed, open the PDF preview
      setPdfPreviewOpen(true);

    } catch (error) {
      console.error("Error updating customer email:", error);
      toast({
        title: "Error",
        description: `Failed to update customer email: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive"
      });
    }
  };

  const handleEmailDialogSubmit = async (email: string) => {
    if (emailDialogAction === 'reminder') {
      await handleEmailReminderSend(email);
    } else if (emailDialogAction === 'receipt') {
      await handleEmailReceiptSend(email);
    }
  };

  // SMS reminders/copies are disabled — email-only flows below.


  const handleSendDocument = async (
    customer: Customer,
    kind: "reminder" | "invoice" | "receipt" | "estimate" | "review"
  ) => {
    if (!user) return;
    if (!customer.email && !customer.phone) {
      toast({
        title: "No contact method",
        description: "This customer has no email or phone on file.",
        variant: "destructive",
      });
      return;
    }

    // Resend just the review link via email + SMS (no PDF)
    if (kind === "review") {
      const reviewUrl = businessProfile?.primary_review_link
        ? getReviewLinkUrl(businessProfile.primary_review_link, businessProfile)
        : undefined;
      if (!reviewUrl) {
        toast({
          title: "No review link",
          description: "Set a primary review link in Review Links first.",
          variant: "destructive",
        });
        return;
      }
      const businessName = businessProfile?.businessName || "Our Business";
      const safeFromName = businessName.replace(/["<>,]/g, "").trim() || "Notifications";
      const customerName = customer.name || "there";
      const text = `Hi ${customerName}, thanks again for choosing ${businessName}! We'd really appreciate a quick review: ${reviewUrl}`;

      const tasks: Promise<unknown>[] = [];
      if (customer.email) {
        tasks.push((async () => {
          try {
            const html = renderPaymentRequestEmailHtml(text, {
              logoUrl: (businessProfile as any)?.companyLogoUrl,
              businessName,
            });
            const result = await sendEmail({
              to: customer.email!,
              subject: `Leave a review for ${businessName}`,
              html,
              from: `${safeFromName} <notifications@houndpayments.co.uk>`,
              userId: user.id,
              emailType: "receipt",
            });
            if (!result.success) throw new Error(result.error || "Failed to send email");
            toast({ title: "Email Sent", description: `Review link sent to ${customer.email}` });
          } catch (err: any) {
            toast({ title: "Email Failed", description: err?.message || "Could not send email", variant: "destructive" });
          }
        })());
      }
      if (customer.phone) {
        tasks.push((async () => {
          try {
            const ok = await sendSmsWithQuotaToast(customer.phone!, text);
            if (ok) toast({ title: 'SMS Sent', description: `Review link sent to ${customer.phone}` });
          } catch (err: any) {
            toast({ title: 'SMS Failed', description: err?.message || 'Could not send SMS', variant: 'destructive' });
          }
        })());
      }
      await Promise.allSettled(tasks);
      return;
    }


    const docType: DocumentType =
      kind === "invoice" ? "invoice" : kind === "receipt" ? "receipt" : kind === "estimate" ? "estimate" : "invoice";
    const meta = getDocumentMeta(docType);
    const savedSettings = loadPaymentSettings(user.id);
    const businessName = businessProfile?.businessName || "Our Business";
    const amount = customer.payment_links?.amount || 0;
    const currency = customer.payment_links?.currency || "GBP";
    const description = customer.payment_links?.description || "";
    const formattedBankDetails = businessProfile?.bankDetails
      ? convertBankDetails(businessProfile.bankDetails)
      : undefined;

    const template =
      kind === "reminder"
        ? savedSettings.paymentReminderTemplate || DEFAULT_PAYMENT_REMINDER_TEMPLATE
        : meta.template(savedSettings);

    const body = renderPaymentRequestTemplate(template, {
      customerName: customer.name || "Customer",
      businessName,
      amount,
      currency,
      description,
      accountName: formattedBankDetails?.accountName,
      accountNumber: formattedBankDetails?.accountNumber,
      sortCode: formattedBankDetails?.sortCode,
      dueDays: savedSettings.paymentDueDays,
    });

    // Copies (reminder/invoice/receipt/estimate) never include the review link —
    // it only goes on the initial payment request and the payment received email.
    const bodyWithReview = body;
    const safeFromName = businessName.replace(/["<>,]/g, "").trim() || "Notifications";

    const labelMap = {
      reminder: "Payment Reminder",
      invoice: "Invoice",
      receipt: "Receipt",
      estimate: "Estimate",
    } as const;
    const label = labelMap[kind];

    // Build shared receipt data (used for both email attachment and SMS link)
    const receiptData: ReceiptData | null =
      kind !== "reminder"
        ? {
            receiptNumber: `${kind.toUpperCase()}-${customer.id.substring(0, 8).toUpperCase()}`,
            date: new Date().toLocaleDateString("en-GB"),
            amount,
            currency,
            description: description || label,
            customerName: customer.name || undefined,
            customerEmail: customer.email || undefined,
            customerPhone: customer.phone || undefined,
            customerAddress:
              customer.address || customer.postcode
                ? { street: customer.address || undefined, postcode: customer.postcode || undefined }
                : undefined,
            businessProfile,
            bankDetails: formattedBankDetails,
            includeAddress: !!(customer.address || customer.postcode),
          }
        : null;

    // Generate PDF once and reuse for email attachment + SMS upload
    let sharedPdfBase64: string | null = null;
    if (receiptData) {
      try {
        sharedPdfBase64 = await meta.generatePdf(receiptData);
      } catch (err) {
        console.error(`Failed to generate ${kind} PDF:`, err);
      }
    }

    const tasks: Promise<unknown>[] = [];

    // Email
    if (customer.email) {
      const emailTask = (async () => {
        try {
          let attachments: any[] | undefined;
          if (sharedPdfBase64) {
            attachments = [
              {
                filename: `${kind}-${customer.name || "customer"}-${customer.id.substring(0, 8)}.pdf`,
                content: sharedPdfBase64.includes(",") ? sharedPdfBase64.split(",")[1] : sharedPdfBase64,
                type: "application/pdf",
              },
            ];
          }

          const html = renderPaymentRequestEmailHtml(bodyWithReview, {
            logoUrl: savedSettings.includeLogo ? (businessProfile as any)?.companyLogoUrl : undefined,
            businessName,
          });

          const result = await sendEmail({
            to: customer.email!,
            subject: `${label} From ${businessName}`,
            html,
            from: `${safeFromName} <notifications@houndpayments.co.uk>`,
            userId: user.id,
            emailType: kind === "reminder" ? "reminder" : "receipt",
            attachments,
          });
          if (!result.success) throw new Error(result.error || "Failed to send email");
          toast({ title: "Email Sent", description: `${label} sent to ${customer.email}` });
        } catch (err: any) {
          console.error(`Email ${kind} failed:`, err);
          toast({
            title: "Email Failed",
            description: err?.message || "Could not send email",
            variant: "destructive",
          });
        }
      })();
      tasks.push(emailTask);
    }

    // SMS (with optional PDF link)
    if (customer.phone) {
      const smsTask = (async () => {
        try {
          let smsBody = bodyWithReview;
          if (sharedPdfBase64 && user?.id) {
            try {
              const base64 = sharedPdfBase64.includes(',') ? sharedPdfBase64.split(',')[1] : sharedPdfBase64;
              const uploadId = `${kind}-${customer.id}-${Date.now()}`;
              const publicUrl = await uploadReceiptPdf(base64, user.id, uploadId);
              if (publicUrl) {
                const shortPdfUrl = await shortenUrl(publicUrl, user.id);
                if (shortPdfUrl) smsBody = `${smsBody}\n\nPDF: ${shortPdfUrl}`;
              }
            } catch (uploadErr) {
              console.error('PDF upload for SMS failed:', uploadErr);
            }
          }
          const ok = await sendSmsWithQuotaToast(customer.phone!, smsBody);
          if (ok) toast({ title: 'SMS Sent', description: `${label} sent to ${customer.phone}` });
        } catch (err: any) {
          console.error(`SMS ${kind} failed:`, err);
          toast({ title: 'SMS Failed', description: err?.message || 'Could not send SMS', variant: 'destructive' });
        }
      })();
      tasks.push(smsTask);
    }

    await Promise.allSettled(tasks);
  };


  const handlePreviewReceipt = (customer: Customer) => {
    setSelectedCustomer(customer);
    setPdfPreviewOpen(true);
  };

  const handleEmailReceipt = (customer: Customer) => {
    setSelectedCustomer(customer);
    
    // If customer has email, open PDF preview directly
    if (customer.email) {
      setPdfPreviewOpen(true);
    } else {
      // If no email, prompt to add email first
      setEmailDialogAction('receipt');
      setEmailDialogOpen(true);
    }
  };

  const handleEmailReceiptFromDialog = async (customer: Customer, pdfData: string) => {
    if (!user) {
      toast({
        title: "Error",
        description: "User information not available",
        variant: "destructive"
      });
      return;
    }

    // If customer doesn't have email, we need to get one first
    if (!customer.email) {
      toast({
        title: "Email Required",
        description: "Please add an email address to this customer first by using the 'Add Email & Send' button",
        variant: "destructive"
      });
      return;
    }

    const result = await sendReceiptEmail(customer, pdfData);

    if (result.success) {
      toast({
        title: "Email Sent",
        description: `Receipt sent successfully to ${customer.email}`,
      });
      setPdfPreviewOpen(false);
    } else {
      throw new Error(result.error || "Failed to send email");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'overdue':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading customers...</p>
        </div>
      </div>
    );
  }

  if (customers.length === 0) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-600 mb-4">No customers found for the selected date range.</p>
        <p className="text-sm text-gray-500">
          Try adjusting your date range or create some payment requests to see customers here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="sticky top-14 md:top-16 z-30 -mx-4 px-4 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border pt-3 pb-3 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Customers</h3>
        </div>

        <CustomerSearch
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          resultCount={filteredCustomers.length}
        />

        <div className="flex flex-wrap items-center gap-2">
          {([
            { key: 'all', label: 'All' },
            { key: 'paid', label: 'Paid' },
            { key: 'overdue', label: 'Overdue' },
            { key: 'estimate', label: 'Estimates' },
          ] as const).map(({ key, label }) => {
            const active = statusFilter === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setStatusFilter(key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                  active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-input hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                {label} <span className="opacity-70">({statusCounts[key]})</span>
              </button>
            );
          })}
        </div>
      </div>

      
      <div className="grid gap-4">
        {filteredCustomers.map((customer) => (
          <CustomerCard
            key={customer.id}
            customer={customer}
            onStatusUpdate={handleStatusUpdate}
            onSendReminder={handleSendReminderDirect}
            onViewStoredPdf={openStoredCustomerPdf}
            onEmailReceipt={handleEmailReceipt}
            onDeleteCustomer={handleDeleteCustomer}
            onEditCustomer={handleEditCustomer}
            onSendDocument={handleSendDocument}
            onConvertEstimate={async (c) => {
              const detail = {
                amount: c.payment_links?.amount,
                currency: c.payment_links?.currency,
                description: c.payment_links?.description || '',
                customerName: c.name || '',
                customerEmail: c.email || '',
                customerPhone: c.phone || '',
                street: c.address || '',
                postcode: c.postcode || '',
              };
              try {
                sessionStorage.setItem('prefillPaymentForm', JSON.stringify(detail));
              } catch (e) {
                console.error('Failed to stash prefill data', e);
              }
              window.dispatchEvent(new CustomEvent('prefillPaymentForm', { detail }));

              // Delete the original estimate customer card — a new payment request
              // card is created on submit, so keeping the estimate would duplicate it.
              try {
                const { error: delErr } = await supabase
                  .from('customers')
                  .delete()
                  .eq('id', c.id);
                if (delErr) {
                  console.error('Failed to delete estimate customer on convert:', delErr);
                } else {
                  setCustomers(prev => prev.filter(x => x.id !== c.id));
                  window.dispatchEvent(new CustomEvent('customerStatusChanged'));
                }
              } catch (e) {
                console.error('Unexpected error deleting estimate on convert:', e);
              }

              toast({
                title: "Estimate converted",
                description: "Details copied to the dashboard — ready to send an invoice or receipt.",
              });
              navigate('/');
            }}

            sendingEmail={sendingEmail}
          />
        ))}
      </div>

      {selectedCustomer && (
        <>
          <EmailDialog
            open={emailDialogOpen}
            onOpenChange={setEmailDialogOpen}
            customerName={selectedCustomer.name || 'Customer'}
            currentEmail={selectedCustomer.email || ''}
            title={emailDialogAction === 'reminder' ? "Send Email Reminder" : "Send Email Receipt"}
            description={emailDialogAction === 'reminder' 
              ? "Send a payment reminder to {customerName}" 
              : "Add email address to send receipt to {customerName}"
            }
            onEmailSubmit={handleEmailDialogSubmit}
          />
          

          <PDFPreviewDialog
            open={pdfPreviewOpen}
            onOpenChange={setPdfPreviewOpen}
            customer={selectedCustomer}
            businessProfile={businessProfile}
            onEmailReceipt={handleEmailReceiptFromDialog}
          />
        </>
      )}

      <EditCustomerDialog
        open={editDialogOpen}
        onOpenChange={(o) => { setEditDialogOpen(o); if (!o) setEditingCustomer(null); }}
        customer={editingCustomer}
        onSave={handleSaveEdit}
        saving={savingEdit}
      />
    </div>
  );
};

export default CustomersList;
